import { CircleCheck } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { type WizardStep, type WizardStepNavMode } from "./types";
import { resolveWizardStepAccess } from "./wizardStepAccess";

interface WizardStepNavProps {
  items: WizardStep[];
  currentStep: number;
  stepValidity: Record<number, boolean>;
  completedSteps?: Record<string, boolean>;
  stepDependencies?: Record<string, string[]>;
  /** When true, only the active step is clickable (backward navigation locked). */
  lockBackNavigation?: boolean;
  /** Sidebar navigation policy — see `WizardStepNavMode`. */
  stepNavMode?: WizardStepNavMode;
  onStepClick: (index: number) => void;
}

export const WizardStepNav: React.FC<WizardStepNavProps> = ({
  items,
  currentStep,
  stepValidity,
  completedSteps,
  stepDependencies,
  lockBackNavigation = false,
  stepNavMode = "backwardOnly",
  onStepClick,
}) => {
  const { t } = useTranslation();
  return (
    <nav className="tlx-panel-wizard-nav" aria-label="Wizard steps">
      {items.map((item, index) => {
        // Single source of truth, shared with `WizardInner.jumpToStep` — see
        // `wizardStepAccess.ts`. Never re-derive clickability locally here.
        const { status, canEnter, blockedReason } = resolveWizardStepAccess({
          index,
          step: item,
          currentStep,
          stepValidity,
          completedSteps,
          stepDependencies,
          lockBackNavigation,
          navMode: stepNavMode,
        });

        const isActive = status === "active";
        const isCompleted = status === "complete";

        const stateClass = isActive
          ? "tlx-panel-wizard-nav__step--active"
          : isCompleted
            ? "tlx-panel-wizard-nav__step--completed"
            : status === "available"
              ? "tlx-panel-wizard-nav__step--available"
              : "tlx-panel-wizard-nav__step--locked";

        // Says *why* the step is unavailable, which the dimmed styling alone
        // cannot. Keys are named after `WizardStepBlockedReason`, so there is no
        // lookup table here to drift out of sync with the union.
        const blockedTitle = blockedReason
          ? t(`slidePanel.stepNav.blocked.${blockedReason}`)
          : undefined;

        const Icon = item.icon;

        return (
          <button
            type="button"
            key={item.key}
            className={`tlx-panel-wizard-nav__step ${stateClass}`}
            onClick={() => canEnter && onStepClick(index)}
            disabled={!canEnter}
            title={blockedTitle}
            aria-current={isActive ? "step" : undefined}
            aria-disabled={!canEnter}
            data-test-id={`wizard-step-nav-${item.key}`}
          >
            <div className="tlx-panel-wizard-nav__icon-wrap">
              {isCompleted ? (
                <CircleCheck size={20} />
              ) : Icon ? (
                <Icon size={20} />
              ) : (
                <span className="tlx-panel-wizard-nav__step-number">
                  {index + 1}
                </span>
              )}
            </div>
            <div className="tlx-panel-wizard-nav__text">
              <div className="tlx-panel-wizard-nav__label-row">
                <p className="tlx-panel-wizard-nav__label">{item.label}</p>
                {item.optional && (
                  <span className="tlx-panel-wizard-nav__optional-badge">
                    {item.optionalLabel ?? t("slidePanel.optional")}
                  </span>
                )}
              </div>
              {item.description && (
                <p className="tlx-panel-wizard-nav__desc">{item.description}</p>
              )}
            </div>
          </button>
        );
      })}
    </nav>
  );
};
